
import React, { useEffect, useState, useCallback, useRef } from 'react';
import NotificationContext from "views/Components/Context.js";
import HeadingWithShortDescription from "components/Heading/HeadingWithShortDescription";
import Button from "components/CustomButtons/Button"
import {
    Box,
    CircularProgress,
    Divider,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Typography,
    createMuiTheme,
    ThemeProvider,
    makeStyles,
} from '@material-ui/core';
import {
    ExpandMore,
    NavigateNext,
    NavigateBefore,
    CloudDownload,
} from '@material-ui/icons';

const theme = createMuiTheme({
    palette: {
        primary: { main: '#20677b' },        // sliders, switches, buttons
        text: { primary: '#000' },           // Typography defaults
    },
});

const useStyles = makeStyles(() => ({
    navBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: '12px 0',
        width: "100%",
        justifyContent: "space-between"
    },
    questionBox: {
        background: '#f7f7f7',
        borderRadius: 4,
        padding: 16,
        whiteSpace: 'pre-wrap',
    },
}));

const fetchFile = async (url) => {
    const formData = new FormData();
    formData.append("url", url);

    try {
        const response = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/fetch_quizdata`, {
            method: "POST",
            body: formData,
        });
        const data = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching file:", err);
    }
};

export default function DataGenerationStep() {
    const [files, setFiles] = useState([]);
    const [loadingAll, setBusy] = useState(true);
    const cancelledRef = useRef(false);
    const { showNotification } = React.useContext(NotificationContext);
    const classes = useStyles();

    const project = {
        name: "Data Project 1",
        description: "Data Project Description 1"

    }


    const fileLinks = [
        { url: 'https://qucoursify.s3.us-east-1.amazonaws.com/qucoursify/EBM_qa_pairs.json', fileName: 'introduction.txt' },
        { url: 'https://qucoursify.s3.us-east-1.amazonaws.com/qucoursify/EBM_cot_examples.json', fileName: 'chapter1.txt' },
    ];

    useEffect(() => {
        (async () => {
            try {
                const results = await Promise.all(
                    fileLinks.map(async ({ url, fileName }) => {
                        try {
                            const res = await fetchFile(url);
                            return { url, fileName, json: res };
                        } catch (error) {
                            return { url, fileName, error };
                        }
                    })
                );
                if (!cancelledRef.current) setFiles(results);
            } finally {
                if (!cancelledRef.current) setBusy(false);
            }
        })();

        return () => { cancelledRef.current = true; };
    }, []);

    if (loadingAll) {
        return (
            <Box mt={8} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={40} />
                <Typography variant='h5' style={{ marginLeft: '10px' }}>Loading Data...</Typography>
            </Box>
        );
    }



    return (
        <ThemeProvider theme={theme}>
            <HeadingWithShortDescription
                title={project.name}
                shortDescription={project.description}
                dialogTitle="Edit Project Details"
            />
            <Divider style={{
                marginTop: "-20px"
            }} />

            <Box mt={2} mb={3}>
                <Typography variant="h5">Data Generation</Typography>
                <Typography variant="body1">
                    Review the generated datafor your files.
                </Typography>
            </Box>


            {files.map(({ url, fileName, json, error }) => (
                <ExpansionPanel key={url} TransitionProps={{ unmountOnExit: true }}>
                    <ExpansionPanelSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                            {fileName}
                            {error && (
                                <Typography component="span" color="error">
                                    &nbsp;– failed to load
                                </Typography>
                            )}
                        </Typography>
                    </ExpansionPanelSummary>

                    <Divider />

                    <ExpansionPanelDetails style={{ display: 'block' }}>
                        {error ? (
                            <Typography color="error">{error.message}</Typography>
                        ) : (
                            <FileTabs file={{ ...json, url }} classes={classes} />
                        )}
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            ))}
        </ThemeProvider>
    );
}


function FileTabs({ file, classes }) {
    const hasQA = Array.isArray(file.qa_pairs) && file.qa_pairs.length > 0;
    const hasCOT = Array.isArray(file.cot_examples) && file.cot_examples.length > 0;

    const tabs = [
        { label: 'Summary', value: 'summary' },
        ...(hasQA ? [{ label: `QA Pairs (${file.qa_pairs.length})`, value: 'qa' }] : []),
        ...(hasCOT ? [{ label: `CoT Pairs (${file.cot_examples.length})`, value: 'cot' }] : []),
    ];


    const [activeTab, setActiveTab] = useState(tabs[0].value);

    return (
        <>
            <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
            >
                {tabs.map((t) => (
                    <Tab key={t.value} label={t.label} value={t.value} />
                ))}
            </Tabs>

            <Divider />

            {activeTab === 'summary' && (
                <Box mt={2}>
                    <Typography className={classes.questionBox}>{file.summary}</Typography>

                    <DownloadButton url={file.url} style={{ marginTop: "20px" }} />
                </Box>
            )}

            {activeTab === 'qa' && (
                <QAPanel
                    items={file.qa_pairs}
                    showReasoning={false}
                    classes={classes}
                    downloadUrl={file.url}
                />
            )}

            {activeTab === 'cot' && (
                <QAPanel
                    items={file.cot_examples}
                    showReasoning={true}
                    classes={classes}
                    downloadUrl={file.url}
                />
            )}
        </>
    );
}

function QAPanel({ items, showReasoning, classes, downloadUrl }) {
    const [idx, setIdx] = useState(0);
    const max = items.length - 1;

    /* guard against bad indices */
    const go = useCallback(
        (n) => setIdx(Math.max(0, Math.min(max, n))),
        [max],
    );

    return (
        <Box mt={2}>
            <Box>
                <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                        <strong>Question:</strong>
                    </Typography>
                    <Typography className={classes.questionBox}>
                        {items[idx].question}
                    </Typography>
                </Box>
                <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                        <strong>Answer:</strong>
                    </Typography>
                    <Typography className={classes.questionBox}>
                        {items[idx].answer}
                    </Typography>
                </Box>
                {showReasoning && (
                    <Box mb={1}>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Reasoning:</strong>
                        </Typography>
                        <Typography className={classes.questionBox}>
                            {items[idx].reasoning}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box className={classes.navBar} style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between"
            }}>
                <Box style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                    width: "100%"
                }}>
                    <Button
                        aria-label="previous"
                        onClick={() => go(idx - 1)}
                        disabled={idx === 0}
                        color="quCreateColor"
                    >
                        <NavigateBefore /> {" "} Previous
                    </Button>
                    <TextField
                        variant="outlined"
                        size="small"
                        style={{ width: 90 }}
                        label="Jump to"
                        type="number"
                        inputProps={{ min: 1, max: max + 1 }}
                        value={idx + 1}
                        onChange={(e) => go(Number(e.target.value) - 1)}
                    />

                    <Typography variant="body1">
                        &nbsp;/ {max + 1}
                    </Typography>

                    <Button
                        aria-label="next"
                        onClick={() => go(idx + 1)}
                        disabled={idx === max}
                        color="quCreateColor"
                    >
                        Next{" "}<NavigateNext />
                    </Button>
                </Box>
                <DownloadButton url={downloadUrl} />
            </Box>
        </Box>
    );
}

function DownloadButton({ url }) {
    const handleDownload = async () => {
        try {

            const data = await fetchFile(url);
            const fileName = url.split('/').pop().replace(/\?.*$/, '');

            const jsonString = JSON.stringify(data, null, 2);

            const blob = new Blob([jsonString], { type: 'application/json' });
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download =
                fileName || (url.split('/').pop().replace(/\?.*$/, '') || 'download.json');
            document.body.appendChild(link);
            link.click();

            URL.revokeObjectURL(blobUrl);
            document.body.removeChild(link);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    return (
        <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            color="quCreateColor"
            onClick={handleDownload}
        >
            Download
        </Button>
    );
}
